/**
 * Block deprecations for groundworx/carousel.
 *
 * v2 → v3: Migrates splideOptions attribute to carouselOptions.
 * Renames perMove → slidesToScroll, omitEnd → containScroll,
 * converts focus → align, drops unused keys.
 */
import { InnerBlocks } from '@wordpress/block-editor';
import { migrateSplideOptions } from './utils/migrate-splide-options';

const v2 = {
	attributes: {
		isPreview: { type: 'boolean', default: false },
		template: { type: 'string', default: 'default' },
		progressColor: { type: 'string' },
		customProgressColor: { type: 'string' },
		progressBackgroundColor: { type: 'string' },
		customProgressBackgroundColor: { type: 'string' },
		paginationColor: { type: 'string' },
		customPaginationColor: { type: 'string' },
		paginationBackgroundColor: { type: 'string' },
		customPaginationBackgroundColor: { type: 'string' },
		paginationBorderColor: { type: 'string' },
		customPaginationBorderColor: { type: 'string' },
		paginationInactiveColor: { type: 'string' },
		customInactivePaginationColor: { type: 'string' },
		customPaginationInactiveColor: { type: 'string' },
		paginationInactiveBackgroundColor: { type: 'string' },
		customPaginationInactiveBackgroundColor: { type: 'string' },
		paginationInactiveBorderColor: { type: 'string' },
		customPaginationInactiveBorderColor: { type: 'string' },
		arrowsColor: { type: 'string' },
		customArrowsColor: { type: 'string' },
		arrowsBackgroundColor: { type: 'string' },
		customArrowsBackgroundColor: { type: 'string' },
		arrowsBorderColor: { type: 'string' },
		customArrowsBorderColor: { type: 'string' },
		counterColor: { type: 'string' },
		customCounterColor: { type: 'string' },
		paginationStyle: { type: 'string', default: 'circleOutline' },
		arrowStyle: { type: 'string', default: 'chevron' },
		breakpoints: {
			type: 'object',
			default: {
				tablet: { layout: { type: 'grid', minimumColumnWidth: null, columnCount: 3 } },
				laptop: { layout: { type: 'grid', minimumColumnWidth: '300px' } },
				desktop: { layout: { type: 'grid', minimumColumnWidth: '300px' } },
			},
		},
		splideOptions: {
			type: 'object',
			default: {
				type: 'slide',
				align: 'start',
				perPage: 1,
				autoplay: false,
				pagination: false,
				arrows: false,
				progressBar: false,
				counter: false,
				omitEnd: true,
			},
		},
	},

	supports: {
		html: false,
		inserter: true,
		className: true,
		align: [ 'full', 'wide' ],
		groundworx: {
			breakpoints: [ 'large-phone', 'tablet', 'laptop', 'desktop', 'large-desktop' ],
		},
		color: {
			gradients: true,
			__experimentalDefaultControls: { background: true, text: true },
		},
		spacing: {
			margin: [ 'top', 'bottom' ],
			padding: true,
			blockGap: true,
			selector: '.gwx-carousel__container',
			__experimentalSkipSerialization: [ 'blockGap' ],
			__experimentalDefaultControls: { padding: true, blockGap: true },
		},
		shadow: true,
		__experimentalBorder: {
			color: true,
			radius: true,
			style: true,
			width: true,
			__experimentalDefaultControls: { color: true, radius: true, style: true, width: true },
		},
		layout: {
			default: { type: 'default' },
			allowJustification: true,
			allowOrientation: true,
			allowSwitching: false,
			allowEditing: true,
			allowSizingOnChildren: false,
			allowVerticalAlignment: true,
		},
		typography: {
			fontSize: true,
			lineHeight: true,
			__experimentalFontFamily: true,
			__experimentalFontWeight: true,
			__experimentalFontStyle: true,
			__experimentalTextTransform: true,
			__experimentalTextDecoration: true,
			__experimentalLetterSpacing: true,
			__experimentalDefaultControls: { fontSize: true },
		},
	},

	isEligible( attributes ) {
		return !! attributes.splideOptions && Object.keys( attributes.splideOptions ).length > 0;
	},

	migrate( attributes ) {
		const { splideOptions, ...rest } = attributes;
		return {
			...rest,
			carouselOptions: migrateSplideOptions( splideOptions ),
			splideOptions: undefined,
		};
	},

	save() {
		return <InnerBlocks.Content />;
	},
};

export default [ v2 ];
